import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Avatar,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import { TRANSFER_TICKET } from "../graphql/mutations";
import { GET_ASSIGNABLE_USERS } from "../graphql/queries";

const TransferTicketDialog = ({
  open,
  onClose,
  onSuccess,
  ticket,
  spaceId,
}) => {
  const [selectedUser, setSelectedUser] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  // Query for users with appropriate permissions
  const {
    data: usersData,
    loading: usersLoading,
    error: usersError,
  } = useQuery(GET_ASSIGNABLE_USERS, {
    variables: { spaceId },
    skip: !open || !spaceId,
  });

  // Transfer mutation
  const [transferTicket, { loading: transferLoading }] = useMutation(
    TRANSFER_TICKET,
    {
      onCompleted: () => {
        handleReset();
        onSuccess();
      },
      onError: (error) => {
        setError(error.message);
      },
    }
  );

  useEffect(() => {
    if (usersError) {
      setError(usersError.message);
    }
  }, [usersError]);

  const handleReset = () => {
    setSelectedUser("");
    setReason("");
    setError("");
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = () => {
    if (!selectedUser) {
      setError("Please select a user to transfer to");
      return;
    }

    // Check if trying to transfer to current assignee
    if (
      ticket?.assignedTo?.id === selectedUser ||
      ticket?.assignedTo?._id === selectedUser
    ) {
      setError("Ticket is already assigned to this user");
      return;
    }

    transferTicket({
      variables: {
        ticketId: ticket.id || ticket._id,
        newAssignee: selectedUser,
        reason: reason.trim() || undefined,
      },
    });
  };

  // Filter out current assignee from the list
  const availableUsers = (usersData?.ticketAssignableUsers || []).filter(
    (user) =>
      user.id !== ticket?.assignedTo?.id &&
      user._id !== ticket?.assignedTo?._id &&
      user.id !== ticket?.createdBy?.id &&
      user._id !== ticket?.createdBy?._id
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Transfer Ticket</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {ticket && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: "background.default",
                borderRadius: 1,
              }}
            >
              <Box sx={{ mb: 1 }}>
                <strong>Ticket:</strong> {ticket.ticketNumber}
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Subject:</strong> {ticket.subject}
              </Box>
              {ticket.assignedTo && (
                <Box>
                  <strong>Current Assignee:</strong> {ticket.assignedTo.name}
                </Box>
              )}
            </Box>
          )}

          {usersLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Transfer To</InputLabel>
                <Select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  label="Transfer To"
                  disabled={transferLoading}
                  renderValue={(value) => {
                    const user = availableUsers.find(
                      (u) => u.id === value || u._id === value
                    );
                    return user ? user.name : "";
                  }}
                >
                  {availableUsers.length === 0 ? (
                    <MenuItem disabled>
                      No users available for transfer
                    </MenuItem>
                  ) : (
                    availableUsers.map((user) => (
                      <MenuItem
                        key={user.id || user._id}
                        value={user.id || user._id}
                      >
                        <Box display="flex" alignItems="center" width="100%">
                          <ListItemAvatar>
                            <Avatar
                              src={user.avatar}
                              alt={user.name}
                              sx={{ width: 32, height: 32 }}
                            >
                              {user.name?.charAt(0)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={user.name}
                            secondary={user.email}
                            primaryTypographyProps={{ variant: "body2" }}
                            secondaryTypographyProps={{ variant: "caption" }}
                          />
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Transfer Reason (Optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                margin="normal"
                multiline
                rows={3}
                disabled={transferLoading}
                helperText="Provide a reason for transferring this ticket"
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={transferLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            transferLoading ||
            usersLoading ||
            !selectedUser ||
            availableUsers.length === 0
          }
          startIcon={transferLoading && <CircularProgress size={20} />}
        >
          {transferLoading ? "Transferring..." : "Transfer Ticket"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferTicketDialog;
